import { useCallback, useEffect, useRef, useState } from "react";
import WindowSearch from "@/components/WindowSearch";
import { toast } from "sonner";
import { X, Lock, Play, Loader2, Check, AlertTriangle, RotateCcw, Download, PackageCheck, Eye, Pause, Square, Zap, Hand } from "lucide-react";
import { api } from "@/lib/api";

const fmtT = (s) => {
  const t = Math.max(0, Number(s) || 0);
  const m = Math.floor(t / 60);
  const sec = (t - m * 60).toFixed(2).padStart(5, "0");
  return `${String(m).padStart(2, "0")}:${sec}`;
};

const STATUS = {
  complete: { icon: Check, cls: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10", label: "Rendered" },
  rendering: { icon: Loader2, cls: "text-[#00F0FF] border-[#00F0FF]/40 bg-[#00F0FF]/10", label: "Rendering", spin: true },
  ready: { icon: Play, cls: "text-white border-white/25 bg-white/5", label: "Ready" },
  dirty: { icon: AlertTriangle, cls: "text-amber-400 border-amber-500/40 bg-amber-500/10", label: "Changed" },
  failed: { icon: AlertTriangle, cls: "text-red-400 border-red-500/40 bg-red-500/10", label: "Failed" },
  locked: { icon: Lock, cls: "text-[#666] border-white/10 bg-transparent", label: "Locked" },
};

export default function PhraseRenderWorkspace({ open, onOpenChange, projectId }) {
  const [man, setMan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assembling, setAssembling] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [gate, setGate] = useState(null);           // {reason, message} when access denied
  const [mode, setMode] = useState("manual");        // manual | automatic
  const [autoRunning, setAutoRunning] = useState(false);
  const [autoPaused, setAutoPaused] = useState(false);
  const [failedSeg, setFailedSeg] = useState(null);
  const pollRef = useRef(null);
  const busyRef = useRef(false);
  const autoRunningRef = useRef(false);
  useEffect(() => { autoRunningRef.current = autoRunning; }, [autoRunning]);
  const BE = process.env.REACT_APP_BACKEND_URL;

  const load = useCallback(async (rebuild) => {
    // NOTE: keep deps as [projectId] only. Do NOT add autoRunning here — a changing `load`
    // identity re-fires the on-open effect which resets autoRunning (breaks Automatic mode).
    try {
      const m = rebuild ? await api.v2BuildSegments(projectId) : await api.v2GetSegments(projectId);
      setMan(m);
      setGate(null);
      if (m.render_mode) setMode((prev) => (autoRunningRef.current ? prev : m.render_mode));
      return m;
    } catch (e) {
      const st = e?.response?.status;
      const data = e?.response?.data?.detail || e?.response?.data;
      if (st === 402 || st === 403) {
        setGate({ reason: st === 402 ? "upgrade_required" : "forbidden", message: (data && data.message) || "Export V2 is not available for your account." });
      } else {
        toast.error("Could not load segments");
      }
      return null;
    }
  }, [projectId]);

  useEffect(() => {
    if (!open) { clearInterval(pollRef.current); return; }
    setResult(null); setLoading(true); setFailedSeg(null); setAutoRunning(false); setAutoPaused(false);
    load(true).finally(() => setLoading(false));
    return () => clearInterval(pollRef.current);
  }, [open, load]);

  // poll while a segment is rendering (or auto run is active between segments)
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!open || !man) return;
    const anyRendering = (man.segments || []).some((s) => s.status === "rendering");
    if (anyRendering || (autoRunning && !autoPaused)) {
      pollRef.current = setInterval(() => load(false), 1500);
    }
    return () => clearInterval(pollRef.current);
  }, [open, man, autoRunning, autoPaused, load]);

  const doAssemble = useCallback(async (m) => {
    setAssembling(true);
    try {
      const r = await api.v2Assemble(projectId, m || mode);
      setResult(r);
      toast.success(`Final export assembled in ${(r.assembly_ms / 1000).toFixed(1)}s`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Assembly failed"); }
    finally { setAssembling(false); }
  }, [projectId, mode]);

  // ---- Automatic controller: one segment at a time, sequential, auto-assemble on completion ----
  useEffect(() => {
    if (!open || mode !== "automatic" || !autoRunning || autoPaused || assembling || result || !man) return;
    const segs = man.segments || [];
    const rendering = segs.some((s) => s.status === "rendering");
    if (rendering || busyRef.current) return;
    const failed = segs.find((s) => s.status === "failed");
    if (failed) { setFailedSeg(failed); setAutoRunning(false); toast.error(`Segment #${failed.index + 1} failed — retry to resume.`); return; }
    const next = segs.find((s) => s.status === "ready" || s.status === "dirty");
    if (next) {
      busyRef.current = true;
      api.v2RenderSegment(projectId, next.segment_id, "automatic")
        .then(() => load(false))
        .catch((e) => { toast.error(e?.response?.data?.detail || "Render failed to start"); setAutoRunning(false); })
        .finally(() => { busyRef.current = false; });
      return;
    }
    const allDone = segs.length > 0 && segs.every((s) => s.status === "complete");
    if (allDone) { setAutoRunning(false); doAssemble("automatic"); }
  }, [open, mode, autoRunning, autoPaused, assembling, result, man, projectId, load, doAssemble]);

  const renderSeg = async (segId) => {
    try {
      await api.v2RenderSegment(projectId, segId, "manual");
      toast.info("Rendering segment…");
      load(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Render failed to start"); }
  };

  const switchMode = async (m) => {
    if (autoRunning) return; // guarded by UI; never switch mid-run
    setMode(m);
    try { await api.v2SetRenderMode(projectId, m); }
    catch (e) {
      const st = e?.response?.status;
      if (st === 402 || st === 403) toast.error(e?.response?.data?.detail?.message || "That render mode isn't available for your account.");
    }
  };

  const startAuto = () => { setFailedSeg(null); setAutoPaused(false); setAutoRunning(true); };
  const pauseAuto = () => setAutoPaused(true);
  const resumeAuto = () => setAutoPaused(false);
  const cancelAuto = () => { setAutoRunning(false); setAutoPaused(false); };
  const retryFailed = () => {
    if (!failedSeg) return;
    setFailedSeg(null); setAutoPaused(false); setAutoRunning(true);
  };

  if (!open) return null;
  const segs = man?.segments || [];
  const done = segs.filter((s) => s.status === "complete").length;
  const failedCount = segs.filter((s) => s.status === "failed").length;
  const anyRendering = segs.some((s) => s.status === "rendering");
  const allDone = segs.length > 0 && done === segs.length;
  const pct = segs.length ? Math.round((done / segs.length) * 100) : 0;
  const currentSeg = segs.find((s) => s.status === "rendering") || segs.find((s) => s.status !== "complete");
  const currentNo = currentSeg ? currentSeg.index + 1 : (allDone ? segs.length : "—");

  return (
    <div className="fixed inset-0 z-[60] flex justify-end" data-testid="v2-workspace">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => onOpenChange(false)} />
      <div className="relative flex h-full w-full max-w-md flex-col border-l border-white/10 bg-[#0a0a0a] shadow-2xl animate-in slide-in-from-right duration-300">
        <WindowSearch />
        {/* header */}
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#00F0FF]">Export Engine V2</div>
            <h2 className="mt-0.5 text-lg font-bold text-white">Phrase Render Workspace</h2>
          </div>
          <button data-testid="v2-close" onClick={() => onOpenChange(false)} className="rounded-lg p-2 text-[#A1A1AA] hover:bg-white/5 hover:text-white"><X size={18} /></button>
        </div>

        {gate ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center" data-testid="v2-gate">
            {gate.reason === "upgrade_required" ? <Zap size={30} className="text-[#00F0FF]" /> : <Lock size={30} className="text-amber-400" />}
            <div className="text-base font-bold text-white">{gate.reason === "upgrade_required" ? "This feature requires Pro" : "Not available"}</div>
            <p className="max-w-xs text-sm text-[#A1A1AA]">{gate.message}</p>
            {gate.reason === "upgrade_required" && (
              <a data-testid="v2-get-pro" href="/pricing" className="mt-1 inline-flex items-center gap-1.5 rounded-xl bg-[#00F0FF] px-4 py-2 text-sm font-bold text-black hover:bg-[#00F0FF]/90"><Zap size={15} /> Get Pro</a>
            )}
          </div>
        ) : (
          <>
            {/* render-mode selector + summary */}
            <div className="border-b border-white/10 px-5 py-3">
              <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[#666]">Render Mode</div>
              <div className="grid grid-cols-2 gap-2" data-testid="v2-mode-toggle">
                <button data-testid="v2-mode-manual" disabled={autoRunning} onClick={() => switchMode("manual")}
                  className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-bold transition-colors disabled:opacity-40 ${mode === "manual" ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}>
                  <Hand size={14} /> Manual
                </button>
                <button data-testid="v2-mode-automatic" disabled={autoRunning} onClick={() => switchMode("automatic")}
                  className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-bold transition-colors disabled:opacity-40 ${mode === "automatic" ? "border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}>
                  <Zap size={14} /> Automatic
                </button>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div><div className="text-[10px] uppercase tracking-wide text-[#666]">Segments</div><div data-testid="v2-sum-total" className="text-sm font-bold text-white">{segs.length}</div></div>
                <div><div className="text-[10px] uppercase tracking-wide text-[#666]">Completed</div><div data-testid="v2-sum-done" className="text-sm font-bold text-white">{done} / {segs.length}</div></div>
                <div><div className="text-[10px] uppercase tracking-wide text-[#666]">Current</div><div data-testid="v2-sum-current" className="text-sm font-bold text-white">{autoRunning || anyRendering ? `#${currentNo}` : "—"}</div></div>
              </div>
            </div>

            {/* progress bar */}
            <div className="border-b border-white/10 px-5 py-3">
              <div className="flex items-center justify-between text-xs text-[#A1A1AA]"><span>Segments rendered</span><span data-testid="v2-progress-count" className="font-bold text-white">{done} / {segs.length}{failedCount ? ` · ${failedCount} failed` : ""}</span></div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-[#00F0FF] transition-all" style={{ width: `${pct}%` }} /></div>
            </div>

            {/* automatic control bar */}
            {mode === "automatic" && (
              <div className="border-b border-white/10 px-5 py-3" data-testid="v2-auto-controls">
                {!autoRunning && !allDone && !failedSeg && (
                  <button data-testid="v2-auto-start" onClick={startAuto} disabled={anyRendering || segs.length === 0}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#00F0FF] px-4 py-2.5 text-sm font-bold text-black hover:bg-[#00F0FF]/90 disabled:opacity-40">
                    <Zap size={16} /> Start Automatic Rendering
                  </button>
                )}
                {autoRunning && !autoPaused && (
                  <div className="flex items-center gap-2">
                    <div className="flex flex-1 items-center gap-2 text-xs font-semibold text-[#00F0FF]"><Loader2 size={14} className="animate-spin" /> Auto-rendering · Segment {currentNo} of {segs.length}</div>
                    <button data-testid="v2-auto-pause" onClick={pauseAuto} className="inline-flex items-center gap-1 rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/5"><Pause size={13} /> Pause</button>
                  </div>
                )}
                {autoRunning && autoPaused && (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 text-xs font-semibold text-amber-300" data-testid="v2-auto-paused">Automatic Rendering Paused</div>
                    <button data-testid="v2-auto-resume" onClick={resumeAuto} className="inline-flex items-center gap-1 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-xs font-bold text-black hover:bg-[#00F0FF]/90"><Play size={13} /> Resume</button>
                    <button data-testid="v2-auto-cancel" onClick={cancelAuto} className="inline-flex items-center gap-1 rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/5"><Square size={13} /> Cancel</button>
                  </div>
                )}
                {failedSeg && (
                  <div className="flex items-center gap-2" data-testid="v2-auto-failed">
                    <div className="flex-1 text-xs font-semibold text-red-300">Segment #{failedSeg.index + 1} failed.</div>
                    <button data-testid="v2-auto-retry" onClick={retryFailed} className="inline-flex items-center gap-1 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-xs font-bold text-black hover:bg-[#00F0FF]/90"><RotateCcw size={13} /> Retry Segment {failedSeg.index + 1}</button>
                  </div>
                )}
              </div>
            )}

            {/* segment list */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loading ? (
                <div className="flex items-center gap-2 py-10 text-sm text-[#A1A1AA]"><Loader2 size={16} className="animate-spin" /> Building segments…</div>
              ) : segs.length === 0 ? (
                <div className="py-10 text-center text-sm text-[#666]">No caption phrases to render.</div>
              ) : (
                <div className="relative">
                  {segs.map((s, i) => {
                    const st = STATUS[s.status] || STATUS.locked;
                    const Icon = st.icon;
                    const canRenderManual = mode === "manual" && (s.status === "ready" || s.status === "dirty" || s.status === "failed") && !anyRendering;
                    return (
                      <div key={s.segment_id} className="relative pb-3" data-testid={`v2-seg-${s.index}`}>
                        {i < segs.length - 1 && <div className={`absolute left-[15px] top-8 h-full w-0.5 ${s.status === "complete" ? "bg-emerald-500/40" : "bg-white/10"}`} />}
                        <div className={`relative flex gap-3 rounded-xl border p-3 ${st.cls}`}>
                          <div className="mt-0.5 shrink-0"><Icon size={18} className={st.spin ? "animate-spin" : ""} /></div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[11px] font-bold uppercase tracking-wide opacity-80" data-testid={`v2-seg-status-${s.index}`}>Segment {s.index + 1} · {st.label}</span>
                              <span className="shrink-0 text-[10px] tabular-nums opacity-60">{fmtT(s.start)} → {fmtT(s.end)}</span>
                            </div>
                            <p className="mt-1 truncate text-sm text-white" title={s.text}>{s.text || <span className="italic opacity-50">(no caption — video/audio only)</span>}</p>
                            {s.status === "rendering" && (
                              <div className="mt-2"><div className="h-1 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-[#00F0FF] transition-all" style={{ width: `${s.progress || 0}%` }} /></div><div className="mt-1 text-[10px] opacity-70">{s.stage || "Rendering"} · {s.progress || 0}%</div></div>
                            )}
                            {s.status === "failed" && s.error && <p className="mt-1 text-[11px] text-red-300">{s.error}</p>}
                            {s.status === "complete" && <p className="mt-1 text-[10px] opacity-60">Rendered {s.render_ms ? `in ${(s.render_ms / 1000).toFixed(1)}s` : ""}</p>}
                            {s.status === "complete" && s.file && preview === s.segment_id && (
                              <video data-testid={`v2-preview-${s.index}`} src={`${BE}/api/media?path=${encodeURIComponent(s.file)}`} controls autoPlay className="mt-2 w-full rounded-lg border border-white/10 bg-black" />
                            )}
                            <div className="mt-2 flex items-center gap-2">
                              {canRenderManual && (
                                <button data-testid={`v2-render-${s.index}`} onClick={() => renderSeg(s.segment_id)}
                                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-xs font-bold text-black hover:bg-[#00F0FF]/90">
                                  {s.status === "failed" ? <><RotateCcw size={13} /> Retry Segment</> : s.status === "dirty" ? <><RotateCcw size={13} /> Re-render</> : <><Play size={13} /> Render Segment</>}
                                </button>
                              )}
                              {s.status === "complete" && s.file && (
                                <button data-testid={`v2-preview-btn-${s.index}`} onClick={() => setPreview(preview === s.segment_id ? null : s.segment_id)}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/5">
                                  <Eye size={13} /> {preview === s.segment_id ? "Hide" : "Preview"}
                                </button>
                              )}
                              {mode === "manual" && s.status === "locked" && <span className="text-[11px] text-[#666]">Render the previous segment first</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* footer: final export */}
            <div className="border-t border-white/10 px-5 py-4">
              {result ? (
                <div data-testid="v2-result" className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-400"><PackageCheck size={16} /> Export Complete · {result.segment_count} segments · assembly {(result.assembly_ms / 1000).toFixed(1)}s</div>
                  <a data-testid="v2-download" href={`${BE}${result.export.mp4_url}`} target="_blank" rel="noreferrer"
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#00F0FF] px-4 py-3 text-sm font-bold text-black hover:bg-[#00F0FF]/90"><Download size={16} /> Download final video</a>
                </div>
              ) : mode === "automatic" ? (
                <div className="text-xs text-[#A1A1AA]">
                  {assembling ? <div className="flex items-center gap-2 text-[#00F0FF]" data-testid="v2-auto-assembling"><Loader2 size={14} className="animate-spin" /> Assembling & validating final video…</div>
                    : allDone ? <div className="flex items-center gap-1.5 font-semibold text-emerald-400"><Check size={14} /> All segments complete — preparing download…</div>
                    : "Automatic rendering will render every segment, then assemble and download automatically."}
                </div>
              ) : (
                <>
                  {allDone ? <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-emerald-400" data-testid="v2-all-rendered"><Check size={14} /> All segments rendered</div>
                    : <div className="mb-2 text-xs text-[#A1A1AA]">Render every segment to enable Final Export.</div>}
                  <button data-testid="v2-final-export" disabled={!allDone || assembling || anyRendering} onClick={() => doAssemble("manual")}
                    className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#00F0FF] px-4 py-3 text-sm font-bold text-black transition-opacity hover:bg-[#00F0FF]/90 disabled:cursor-not-allowed disabled:opacity-40">
                    {assembling ? <><Loader2 size={16} className="animate-spin" /> Assembling…</> : <><PackageCheck size={16} /> Final Export (fast assembly)</>}
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
